import { useState } from 'react';
import { ReportModal } from '@/pages/GamAdvertisers/components/ReportModal';
import type { CacBasis, LLPageRow } from '@/types/lead-lifetime';
import { useMoney } from './currency';
import { BASIS_LABEL, cacKey, fmtLL } from './format';

const INITIAL_ROWS = 10;

interface PagesTableProps {
    pages: LLPageRow[];
    basis: CacBasis;
    selectedPageId?: string;
    onSelect: (pageId: string) => void;
}

function Table({ pages, basis, selectedPageId, onSelect }: PagesTableProps) {
    const money = useMoney();
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="px-3 py-2">Página</th>
                        <th className="px-3 py-2 text-right">Gasto</th>
                        <th className="px-3 py-2 text-right">Leads</th>
                        <th className="px-3 py-2 text-right">Conversas</th>
                        <th className="px-3 py-2 text-right">CAC ({BASIS_LABEL[basis]})</th>
                        <th className="px-3 py-2 text-right">Receita até agora</th>
                        <th className="px-3 py-2 text-right">ROI até agora</th>
                    </tr>
                </thead>
                <tbody>
                    {pages.map((p) => (
                        <tr
                            key={p.page_id}
                            onClick={() => onSelect(p.page_id)}
                            className={`cursor-pointer border-b border-border/60 tabular-nums hover:bg-accent/60 ${p.page_id === selectedPageId ? 'bg-accent' : ''}`}
                        >
                            <td className="max-w-[280px] truncate px-3 py-2 font-medium" title={p.page_id}>{p.page_name}</td>
                            <td className="px-3 py-2 text-right">{money.fmt(p.spend)}</td>
                            <td className="px-3 py-2 text-right">{fmtLL.num(p.new_leads)}</td>
                            <td className="px-3 py-2 text-right">{fmtLL.num(p.conversations)}</td>
                            <td className="px-3 py-2 text-right">{money.unit(p[cacKey(basis)])}</td>
                            <td className="px-3 py-2 text-right">{money.fmt(p.revenue_to_date)}</td>
                            <td className={`px-3 py-2 text-right ${p.roi_to_date != null && p.roi_to_date >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                {fmtLL.pct(p.roi_to_date)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Comparativo por página (fim da tela). Com uma página selecionada, ela aparece destacada
 * entre as demais; clicar numa linha passa a analisar aquela página.
 */
export function PagesTable(props: PagesTableProps) {
    const [showAll, setShowAll] = useState(false);
    const { pages } = props;
    return (
        <div className="rounded-lg border border-border bg-card">
            <div className="border-b border-border px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">Comparativo por página</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground/80">Receita e ROI das coortes que entraram no período · clique numa página para analisá-la</p>
            </div>
            <Table {...props} pages={pages.slice(0, INITIAL_ROWS)} />
            {pages.length > INITIAL_ROWS && (
                <div className="px-4 py-2 text-center">
                    <button type="button" onClick={() => setShowAll(true)} className="rounded-md border border-border px-3 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground">
                        Ver todas as páginas ({pages.length})
                    </button>
                </div>
            )}
            <ReportModal open={showAll} onClose={() => setShowAll(false)} title="Todas as páginas" subtitle={`${pages.length} páginas · ordenadas por gasto`}>
                <Table {...props} onSelect={(id) => { setShowAll(false); props.onSelect(id); }} />
            </ReportModal>
        </div>
    );
}
